import { useEffect, useState, useMemo } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import Logo from "@/components/Logo";
import { Progress } from "@/components/ui/progress";
import { toast } from "sonner";
import { CheckCircle2, Circle, ChevronLeft, ChevronRight, ArrowLeft, Download, Menu, FileText, Award } from "lucide-react";

export default function CoursePlayer() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [completed, setCompleted] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  useEffect(() => {
    api.learnCourse(slug).then((d) => {
      setData(d);
      setCompleted(d.completed_lessons || []);
      const flat = d.course.curriculum.flatMap((s) => s.lessons);
      setActiveId(d.last_lesson_id || flat[0]?.id);
    }).catch(() => setData(false));
  }, [slug]);

  const flat = useMemo(() => (data && data !== false ? data.course.curriculum.flatMap((s) => s.lessons) : []), [data]);
  const idx = flat.findIndex((l) => l.id === activeId);
  const lesson = flat[idx];

  if (data === false) return <div className="min-h-screen grid place-items-center bg-asf-app text-slate-500">You are not enrolled in this course. <Link to="/dashboard" className="text-asf-orange ml-2">Dashboard</Link></div>;
  if (!data || !lesson) return <div className="min-h-screen grid place-items-center bg-asf-app text-slate-500">Loading…</div>;

  const total = data.total_lessons;
  const pct = Math.round((completed.length / total) * 100);

  const markComplete = async () => {
    const res = await api.progress({ course_slug: slug, lesson_id: lesson.id, completed: true });
    setCompleted(res.completed_lessons);
    if (res.certificate) {
      toast.success("🎓 Course complete! Certificate issued.");
    } else {
      toast.success("Lesson marked complete");
      if (idx < flat.length - 1) setActiveId(flat[idx + 1].id);
    }
  };

  const go = (d) => { const n = idx + d; if (n >= 0 && n < flat.length) setActiveId(flat[n].id); };

  return (
    <div className="min-h-screen bg-asf-app flex flex-col" data-testid="course-player">
      {/* top bar */}
      <div className="glass sticky top-0 z-40 px-4 h-14 flex items-center justify-between">
        <div className="flex items-center gap-4">
          <button onClick={() => setSidebarOpen((v) => !v)} className="text-slate-600"><Menu className="h-5 w-5" /></button>
          <Logo compact />
          <span className="hidden sm:block text-sm text-slate-600 font-medium truncate max-w-xs">{data.course.title}</span>
        </div>
        <Link to="/dashboard" className="text-sm text-slate-600 flex items-center gap-1 hover:text-slate-900"><ArrowLeft className="h-4 w-4" /> Dashboard</Link>
      </div>

      <div className="flex-1 flex">
        {/* sidebar curriculum */}
        {sidebarOpen && (
          <aside className="w-80 shrink-0 border-r border-slate-200 bg-asf-surface overflow-y-auto max-h-[calc(100vh-3.5rem)]" data-testid="player-curriculum">
            <div className="p-4 border-b border-slate-200">
              <div className="flex justify-between text-xs text-slate-500 mb-2"><span>Progress</span><span>{pct}%</span></div>
              <Progress value={pct} className="h-1.5 bg-slate-200" />
            </div>
            {data.course.curriculum.map((s, si) => (
              <div key={s.id} className="border-b border-slate-100">
                <div className="px-4 py-3 text-xs font-semibold text-slate-500"><span className="font-mono text-asf-orange mr-2">S{si + 1}</span>{s.title}</div>
                {s.lessons.map((l) => {
                  const done = completed.includes(l.id);
                  const active = l.id === activeId;
                  return (
                    <button key={l.id} data-testid={`lesson-item-${l.id}`} onClick={() => setActiveId(l.id)}
                      className={`w-full text-left px-4 py-3 flex items-start gap-3 text-sm transition-colors ${active ? "bg-asf-orange/10 text-asf-orange" : "text-slate-600 hover:bg-slate-100"}`}>
                      {done ? <CheckCircle2 className="h-4 w-4 text-green-400 shrink-0 mt-0.5" /> : <Circle className="h-4 w-4 text-slate-600 shrink-0 mt-0.5" />}
                      <span className="flex-1">{l.title}</span>
                      <span className="text-xs text-slate-500">{l.duration_min}m</span>
                    </button>
                  );
                })}
              </div>
            ))}
          </aside>
        )}

        {/* main */}
        <div className="flex-1 flex flex-col min-w-0">
          <div className="flex-1 overflow-y-auto max-h-[calc(100vh-7rem)]">
            <div className="bg-black aspect-video max-h-[60vh] w-full">
              <video key={lesson.id} controls className="h-full w-full" poster={data.course.image}>
                <source src={lesson.video_url} type="video/mp4" />
              </video>
            </div>
            <div className="max-w-3xl mx-auto px-6 py-8">
              <div className="asf-overline mb-2">Lesson {idx + 1} of {flat.length}</div>
              <h1 className="font-display text-2xl font-bold">{lesson.title}</h1>
              <p className="text-slate-600 mt-4 leading-relaxed">{lesson.content}</p>
              {lesson.resources?.length > 0 && (
                <div className="mt-6">
                  <h3 className="text-sm font-semibold text-slate-600 mb-2 flex items-center gap-2"><FileText className="h-4 w-4 text-asf-orange" /> Resources</h3>
                  {lesson.resources.map((r) => (
                    <a key={r.title} href={r.url} className="flex items-center gap-2 text-sm text-asf-orange py-1"><Download className="h-4 w-4" /> {r.title}</a>
                  ))}
                </div>
              )}
              {pct === 100 && (
                <Link to="/dashboard" className="mt-8 rounded-2xl bg-asf-orange/10 border border-asf-orange/30 p-5 flex items-center gap-3">
                  <Award className="h-8 w-8 text-asf-orange" />
                  <div><div className="font-semibold">Course complete!</div><div className="text-sm text-slate-500">Your certificate is in your dashboard.</div></div>
                </Link>
              )}
            </div>
          </div>

          {/* bottom bar */}
          <div className="glass border-t border-slate-200 px-4 py-3 flex items-center justify-between gap-3">
            <button data-testid="prev-lesson" onClick={() => go(-1)} disabled={idx === 0} className="btn-secondary px-4 py-2.5 text-sm inline-flex items-center gap-1 disabled:opacity-40"><ChevronLeft className="h-4 w-4" /> Prev</button>
            <button data-testid="mark-complete" onClick={markComplete} className="btn-primary px-6 py-2.5 text-sm inline-flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4" /> {completed.includes(lesson.id) ? "Completed" : "Mark Complete"}
            </button>
            <button data-testid="next-lesson" onClick={() => go(1)} disabled={idx === flat.length - 1} className="btn-secondary px-4 py-2.5 text-sm inline-flex items-center gap-1 disabled:opacity-40">Next <ChevronRight className="h-4 w-4" /></button>
          </div>
        </div>
      </div>
    </div>
  );
}
